import { apiClient } from '@/shared/api/client';
import { API_ENDPOINTS } from '@/shared/constants/api/endpoints.ts';

import type {
  PumsemDivision,
  PumsemEdition,
  PumsemItem,
  PumsemSearchParams,
  PumsemSearchResult,
  PumsemTree,
} from '../model/pumsem.type';
import type {
  PumsemDivisionInput,
  PumsemDivisionPatch,
  PumsemEditionDivision,
  PumsemEditionInput,
  PumsemEditionPatch,
  PumsemGroupInput,
  PumsemGroupOrderInput,
  PumsemGroupPatch,
  PumsemImportPayload,
  PumsemImportResult,
  PumsemItemInput,
  PumsemRevisionDetail,
} from '../model/pumsemManage.type';

/**
 * 품셈해설 관리 호출부 (ADMIN 이상).
 *
 * 읽기(`pumsem.api.ts`)와 파일을 나눈 이유는 배럴이다 — 관리 화면만 쓰는
 * 코드가 읽기 배럴에 섞이면 `/pumsem` 을 여는 방문자에게도 관리 코드가 같이
 * 내려간다(도움말이 `index.ts`/`manage.ts` 를 가른 것과 같은 이유).
 *
 * `skipAuthRedirect` 를 켜지 **않는다** — 여기는 로그인이 전제라, 세션이
 * 끊겼으면 로그인 화면으로 보내는 게 맞다.
 */
/** 일괄 임포트 요청 한도. 게이트웨이 `BULK_PROXY_TIMEOUT_MS`(60초)보다 길다. */
const IMPORT_TIMEOUT_MS = 90_000;

export const pumsemManageApi = {
  /**
   * 숨긴 판까지. 공개 목록과 경로가 다르다 — Gateway 가 public 경로에
   * 사용자 헤더를 안 심어서, 공개 경로는 관리자를 알아볼 수 없다.
   */
  getEditions: async (): Promise<PumsemEdition[]> => {
    const { data } = await apiClient.get<PumsemEdition[]>(
      API_ENDPOINTS.pumsem.manageEditions,
    );
    return data;
  },

  /** 관리 트리. 항 노드에 `revisionId` 가 실려 온다(공개 트리에는 없다). */
  getTree: async (edition?: string): Promise<PumsemTree> => {
    const { data } = await apiClient.get<PumsemTree>(
      API_ENDPOINTS.pumsem.manageTree,
      { params: { edition } },
    );
    return data;
  },

  /**
   * 숨긴 판까지 훑는 검색·상세. 뷰어의 관리자 오버레이가 쓴다 —
   * 화면은 그대로인데 출처만 바뀐다.
   */
  search: async (params: PumsemSearchParams): Promise<PumsemSearchResult> => {
    const { data } = await apiClient.get<PumsemSearchResult>(
      API_ENDPOINTS.pumsem.manageSearch,
      { params },
    );
    return data;
  },

  getItem: async (
    division: string,
    code: string,
    edition?: string,
  ): Promise<PumsemItem> => {
    const { data } = await apiClient.get<PumsemItem>(
      API_ENDPOINTS.pumsem.manageItem,
      { params: { division, code, edition } },
    );
    return data;
  },

  /** 전역 부문 목록. 이 판에 안 실린 부문을 다시 실으려면 이게 있어야 한다. */
  getDivisions: async (): Promise<PumsemDivision[]> => {
    const { data } = await apiClient.get<PumsemDivision[]>(
      API_ENDPOINTS.pumsem.manageDivisions,
    );
    return data;
  },

  /** 이 판에 실린 부문 + 순서 + 이 판에서의 항목 수. */
  getEditionDivisions: async (
    edition?: string,
  ): Promise<PumsemEditionDivision[]> => {
    const { data } = await apiClient.get<PumsemEditionDivision[]>(
      API_ENDPOINTS.pumsem.manageEditionDivisions,
      { params: { edition } },
    );
    return data;
  },

  getRevision: async (revisionId: string): Promise<PumsemRevisionDetail> => {
    const { data } = await apiClient.get<PumsemRevisionDetail>(
      API_ENDPOINTS.pumsem.revision(revisionId),
    );
    return data;
  },

  createItem: async (
    edition: string,
    input: PumsemItemInput,
  ): Promise<PumsemRevisionDetail> => {
    const { data } = await apiClient.post<PumsemRevisionDetail>(
      API_ENDPOINTS.pumsem.createItem(edition),
      input,
    );
    return data;
  },

  updateItem: async (
    revisionId: string,
    input: PumsemItemInput,
  ): Promise<PumsemRevisionDetail> => {
    const { data } = await apiClient.put<PumsemRevisionDetail>(
      API_ENDPOINTS.pumsem.revision(revisionId),
      input,
    );
    return data;
  },

  /** 이 판에서만 뺀다. 항목 자체와 다른 판의 내용은 남는다. */
  removeItem: async (revisionId: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.pumsem.revision(revisionId));
  },

  createEdition: async (input: PumsemEditionInput): Promise<PumsemEdition> => {
    const { data } = await apiClient.post<PumsemEdition>(
      API_ENDPOINTS.pumsem.createEdition,
      input,
    );
    return data;
  },

  /** 표기 이름만 고친다. `slug`(2026-1)는 딥링크라 여기서 못 바꾼다. */
  updateEdition: async (
    slug: string,
    input: PumsemEditionPatch,
  ): Promise<PumsemEdition> => {
    const { data } = await apiClient.put<PumsemEdition>(
      API_ENDPOINTS.pumsem.updateEdition(slug),
      input,
    );
    return data;
  },

  /**
   * 공개 / 숨김.
   *
   * 발행 취소가 아니다 — 최초 공개 시각은 서버가 그대로 둔다. 오탈자를
   * 발견했을 때 내렸다가 고쳐서 다시 올리는 동선이 이걸로 성립한다.
   */
  setEditionVisibility: async (
    slug: string,
    visible: boolean,
  ): Promise<PumsemEdition> => {
    const { data } = await apiClient.patch<PumsemEdition>(
      API_ENDPOINTS.pumsem.editionVisibility(slug),
      { visible },
    );
    return data;
  },

  /**
   * 판 삭제 — 장·절·항목 스냅샷까지 서버가 한 트랜잭션에서 소프트 삭제한다.
   * 공개 중인 판은 서버가 거절한다(먼저 숨겨야 한다).
   */
  removeEdition: async (slug: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.pumsem.updateEdition(slug));
  },

  /**
   * 일괄 임포트 — **부문 하나를 통째로 갈아끼운다.**
   *
   * 서버가 숨긴 판에서만 받아준다. 부분 갱신이 아닌 이유는 직전 판에만 있던
   * 항목을 `REMOVED` 로 표시하기 위해서다 — 항목 단위 upsert 로 두면 사라진
   * 품이 조용히 살아남는다.
   */
  importDivision: async (
    edition: string,
    payload: PumsemImportPayload,
  ): Promise<PumsemImportResult> => {
    const { data } = await apiClient.post<PumsemImportResult>(
      API_ENDPOINTS.pumsem.importEdition(edition),
      payload,
      // 기본 15초로는 부족하다 — 수 MB 를 올리고, 서버가 수백 항목을 한
      // 트랜잭션에서 갈아끼운다. **게이트웨이 한도(60초)보다 길어야** 한다.
      // 짧으면 브라우저가 먼저 포기해서, 서버 트랜잭션은 그대로 도는데
      // 화면만 실패로 끝나고 사용자가 같은 임포트를 또 누른다.
      { timeout: IMPORT_TIMEOUT_MS },
    );
    return data;
  },

  /** 이 판에 부문을 싣는다. 부문 자체를 만드는 것과 다르다. */
  attachDivision: async (edition: string, division: string): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.pumsem.editionDivisions(edition), {
      division,
    });
  },

  /** 이 판에서 뺀다. 부문도 항목도 안 지운다 — 편성만 끊는다. */
  detachDivision: async (edition: string, division: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.pumsem.editionDivisions(edition), {
      params: { division },
    });
  },

  /** 전체 순서를 통째로. 개별 이동 API 는 동시 수정에서 어긋난다. */
  reorderDivisions: async (
    edition: string,
    divisions: string[],
  ): Promise<void> => {
    await apiClient.patch(API_ENDPOINTS.pumsem.editionDivisionOrder(edition), {
      divisions,
    });
  },

  createDivision: async (
    input: PumsemDivisionInput,
  ): Promise<PumsemDivision> => {
    const { data } = await apiClient.post<PumsemDivision>(
      API_ENDPOINTS.pumsem.createDivision,
      input,
    );
    return data;
  },

  updateDivision: async (
    slug: string,
    input: PumsemDivisionPatch,
  ): Promise<PumsemDivision> => {
    const { data } = await apiClient.put<PumsemDivision>(
      API_ENDPOINTS.pumsem.division(slug),
      input,
    );
    return data;
  },

  /** 모든 판을 통틀어 항목이 0건일 때만. slug 는 딥링크의 첫 세그먼트다. */
  removeDivision: async (slug: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.pumsem.division(slug));
  },

  /** 코드를 안 보낸다 — `parentCode` 아래 다음 번호를 서버가 매긴다. */
  createGroup: async (
    edition: string,
    input: PumsemGroupInput,
  ): Promise<{ code: string; title: string }> => {
    const { data } = await apiClient.post<{ code: string; title: string }>(
      API_ENDPOINTS.pumsem.groups(edition),
      input,
    );
    return data;
  },

  updateGroup: async (
    edition: string,
    input: PumsemGroupPatch,
  ): Promise<{ code: string; title: string }> => {
    const { data } = await apiClient.put<{ code: string; title: string }>(
      API_ENDPOINTS.pumsem.groups(edition),
      input,
    );
    return data;
  },

  /** 재정렬은 코드를 다시 매긴다 — 서버가 숨긴 판에서만 받아준다. */
  reorderGroups: async (
    edition: string,
    input: PumsemGroupOrderInput,
  ): Promise<void> => {
    await apiClient.patch(API_ENDPOINTS.pumsem.groupOrder(edition), input);
  },

  removeGroup: async (
    edition: string,
    division: string,
    code: string,
  ): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.pumsem.groups(edition), {
      params: { division, code },
    });
  },
};
